import { coreConfig } from '../config'
import { ApiError, TransportError, type ErrorEnvelope } from './errors'
import { sessionHeaders } from '../session'

/* The one place that talks to the server.

   Everything above this file deals in typed results and typed errors; nothing above it
   knows about fetch, status codes or headers. That is what makes the offline queue
   possible later without rewriting every screen. */

export const CORRELATION_HEADER = 'X-Correlation-Id'

/* Read per call rather than captured at module load: a module-level constant would
   freeze whatever configureCore() had been given at import time, which in a test is
   usually nothing. */
const base = () => coreConfig().apiBaseUrl

/**
 * RFC 4122 v4. Sent on every request so one trace spans the client, the server and the
 * log line a support call starts from.
 *
 * NOT `crypto.randomUUID()`. That exists in a browser and, at the time of writing, not
 * in React Native's Hermes runtime — a shared client that called it would work in
 * every test and throw on the first request a handset ever made. `getRandomValues` is
 * available in both (Expo installs it), and the last resort is `Math.random`, which is
 * acceptable here and nowhere else: a correlation id needs to be unique enough to find
 * a log line, not unguessable.
 */
function newCorrelationId(): string {
  const random = globalThis.crypto?.getRandomValues?.bind(globalThis.crypto)

  const bytes = new Uint8Array(16)
  if (random) random(bytes)
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)

  // Version 4, variant 1, per RFC 4122 section 4.4.
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Supplied when retrying a queued action, so the retry keeps the original trace. */
  correlationId?: string
  /**
   * Extra headers for THIS request only. Used by sign-in for the staging site key,
   * which is deliberately not held by the session: a key that lives in memory for the
   * life of the application is a key that ends up in a crash report.
   */
  headers?: Record<string, string>
  /**
   * A body sent AS IT IS, with no JSON encoding — one photograph, with its own type in
   * `contentType`.
   *
   * Deliberately not multipart. A single file per request has nothing to separate from
   * anything else, so the envelope would be pure overhead — and multipart means the
   * server buffers to parse, which for an eight-megabyte upload is either a temp file or
   * the managed heap. Raw goes straight from the camera to the disk.
   *
   * Mutually exclusive with `body`; passing both is a mistake this type cannot express,
   * so `request` throws on it rather than silently preferring one.
   */
  rawBody?: BodyInit
  contentType?: string
  /**
   * What comes BACK. Defaults to JSON; `'blob'` is for the one endpoint that returns
   * bytes — a photograph — where parsing the body as text would corrupt it and then
   * fail to parse.
   */
  expect?: 'json' | 'blob'
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const correlationId = options.correlationId ?? newCorrelationId()

  if (options.body !== undefined && options.rawBody !== undefined)
    throw new Error('request: pass body OR rawBody, not both.')

  let response: Response
  try {
    response = await fetch(`${base()}${path}`, {
      method: options.method ?? 'GET',
      headers: {
        Accept: 'application/json',
        [CORRELATION_HEADER]: correlationId,
        // Who is asking. Added here, once, rather than at ~20 call sites — a header
        // added per call is a header forgotten on the one endpoint that mattered.
        ...sessionHeaders(),
        ...(options.headers ?? {}),
        ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(options.contentType === undefined ? {} : { 'Content-Type': options.contentType }),
      },
      body:
        options.rawBody !== undefined ? options.rawBody
        : options.body === undefined ? undefined
        : JSON.stringify(options.body),
      signal: options.signal ?? null,
    })
  } catch (cause) {
    // No response at all: offline, DNS, TLS, a dropped connection. There is no code to
    // translate because nothing on the server formed an opinion about this request.
    throw new TransportError('The server could not be reached.', cause)
  }

  if (response.status === 204) return undefined as T

  /* THE BYTES ARE READ AS BYTES.

     Read as text first, a JPEG comes back through the platform's UTF-8 decoder with
     every invalid sequence replaced — so the file is silently corrupted BEFORE anything
     notices, and the only symptom is a picture that will not display. An error path
     still parses as JSON, because a failure is an envelope whatever the caller expected. */
  if (options.expect === 'blob' && response.ok) return (await response.blob()) as T

  const text = await response.text()

  if (!response.ok) {
    let envelope: ErrorEnvelope | null = null
    try {
      envelope = text ? (JSON.parse(text) as ErrorEnvelope) : null
    } catch {
      envelope = null
    }

    // A non-OK response we cannot read as an envelope is NOT an ApiError. It usually
    // means a proxy or gateway answered instead of the application, and inventing a
    // code for it would put a fabricated key into the translation catalogue.
    if (!envelope || typeof envelope.code !== 'string') {
      throw new TransportError(
        `The server returned ${response.status} without an error envelope.`)
    }

    throw new ApiError(
      { ...envelope, correlationId: envelope.correlationId ?? correlationId },
      response.status)
  }

  try {
    return (text ? JSON.parse(text) : undefined) as T
  } catch (cause) {
    throw new TransportError('The server returned a response that could not be read.', cause)
  }
}
